import React, { memo, useEffect } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Slider,
    Tooltip,
    Typography,
} from '@mui/material';
import { createCamera, getCamera } from '@api/camera';
import FVector from '@data/geometry/FVector';
import { fromCurrentSceneCoordinate, inferStoredCoordinateSystem, toCurrentSceneCoordinate } from '@data/geodata/CoordinateSystemUtils';
import { Panels } from '@gui/constants/Panels';
import useNotificationStore from '@gui/store/notification';
import { POVType } from '@rendering/constants/cameraTypes';
import { NotificationType, CameraMode } from '@visualizer/types';
import Grid from '../../../Geodata/grid';
import { getGrid } from '../../../store/LoadingStore';
import useViewerStore, { getViewer } from '../../../store/viewerStore';
import usePersistentStore, { getGlobalState, getNewColorScheme, useGlobalBucket } from '../../store/persistent/persistentStore';
import CameraDialog from './camera/CameraDialog';
import SaveCameraDialog from './camera/SaveCameraDialog';
import type { AmbientState, Camera as CameraState } from '@visualizer/types';
import CustomTooltip from '../generic/CustomTooltip';
import { getSentence } from '@gui/constants/sentences';
import { useCapabilities } from '../../../demo/capabilities';
import { refreshContext } from 'Graphics/standardRenderer';
import { getDisabledInEnvMessage, useEnvStore } from 'store/EnvStore';

type AmbientProps = {
    showContent: boolean;
};

const exported_camera_key = 'exported_camera';

const Camera: React.FC<AmbientProps> = ({ showContent }) => {
    const setPanel = usePersistentStore((s) => s.setPanel);
    const ambient = useGlobalBucket((s) => s.ambient);
    const simulationItems = usePersistentStore((s) => s.getActiveLayerState()?.simulationItems);
    const setAmbientPanel = (updates: Partial<AmbientState>) => setPanel(updates, Panels.AMBIENT);

    const viewer = useViewerStore((state) => state.viewer);
    if (!viewer) return null;

    const { x, y, z } = simulationItems ?? { x: 0, y: 0, z: 0 };
    const { currentCamera } = ambient;
    const [isExportingCamera, setIsExportingCamera] = React.useState(localStorage.getItem(exported_camera_key) != null);
    //const [lockPovVerticalRotation, setLockPovVerticalRotation] = React.useState(true);
    //const [lockPovHorizontalRotation, setLockPovHorizontalRotation] = React.useState(true);
    const [POVCameraType, setPOVCameraType] = React.useState<POVType>(POVType.DATA);
    const [cameraDialogOpen, setCameraDialogOpen] = React.useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = React.useState(false);
    const [showAdvanced, setShowAdvanced] = React.useState(false);
    const showNotification = useNotificationStore((state) => state.showNotification);
    const capabilities = useCapabilities();
    const env = useEnvStore((s) => s.env);
    const previousModeRef = React.useRef(ambient.mode);

    const [orbitate, setOrbitate] = React.useState(false);

    useEffect(() => {
        viewer.cameraHelper.setOrbitationEnabled(orbitate);
    }, [orbitate, viewer]);

    const setCurrentCamera = (camera: CameraState) => {
        setAmbientPanel({ currentCamera: camera });
    };

    const handleCameraMode = (cameraType: CameraMode) => {
        viewer.cameraHelper.setCameraMode(currentCamera, cameraType);

        setCurrentCamera({ ...currentCamera, type: cameraType });

        if (cameraType === CameraMode.POV) {
            resetPov();
            // disable selected
        }
    };

    useEffect(() => {
        // Properly update materials on dispaly mode change:
        const displayModeChanged = previousModeRef.current !== ambient.mode;
        previousModeRef.current = ambient.mode;

        if (displayModeChanged) {
            const isRenderDisplay = ambient.mode === 'render' || ambient.mode === 'hybrid';
            const shadowBuilder = getViewer().shadowBuilder;

            // 1) Enable shadows for render mode:
            if (isRenderDisplay) shadowBuilder?.enable();
            else shadowBuilder?.dispose();

            // 2) Refresh context to update color scheme based on new mode:
            refreshContext(getNewColorScheme(), ambient.mode, getGlobalState().context);

            // Done:
            return;
        }

        getGrid()
            .getActiveGeodata()
            .getIteratorLegacy()
            .forEach((geoObject) => {
                geoObject.toggleVisibility();
            });

        const isRenderDisplay = ambient.mode === 'render' || ambient.mode === 'hybrid';
        if (isRenderDisplay) getViewer().shadowBuilder?.enable();
        else getViewer().shadowBuilder?.dispose();

        getGrid()
            .getActiveGeodata()
            .getIterator()
            .forEach((geoObject) => {
                geoObject.setMode(ambient.mode);
            });

        // Update ground on mode or stroke visibility change (update shaders):
        const ground = getGrid().getActiveGeodata().dataObjectLegacy.Ground;
        if (ground) {
            ground.updateTerrainlineShader();
            ground.toggleVisibility();
        }
    }, [ambient.mode, ambient.showStrokes]);

    const resetPov = () => {
        const activeIndex = Grid.xyzToIndex(x, y, z);
        const sensorPoint = Grid.sensorPoints[activeIndex];
        if (!sensorPoint) return;

        const center = sensorPoint.getCenter();
        const { ID } = sensorPoint;

        let activeSensorData = getGrid().getActiveLayer().getActiveFrame().getSensors()[ID];
        let direction: FVector = activeSensorData?.getVectorialResult()?.normalize() ?? new FVector();

        viewer.cameraHelper.getCustomCamera(CameraMode.POV).setPositionAndTarget(center, direction);
    };

    const updatePovMode = (povType: POVType) => {
        const pov = viewer.cameraHelper.getPovCamera();

        switch (povType) {
            case POVType.DATA:
            case POVType.LOCKED:
                pov.updateLocks(true, true);
                return;
            case POVType.HORIZONTAL:
                pov.updateLocks(false, true);
                return;
            case POVType.VERTICAL:
                pov.updateLocks(true, false);
                return;
            case POVType.FREE:
                pov.updateLocks(false, false);
                return;
        }
    };

    const getSelectedSensorPoint = () => {
        const activeIndex = Grid.xyzToIndex(x, y, z);
        return Grid.sensorPoints[activeIndex];
    };

    const getCurrentPovDirection = () => {
        const { position, target } = viewer.cameraHelper.getPovCamera().getPositionAndTarget();
        const sceneDirection = target.clone().sub(position).normalize();

        return new FVector(sceneDirection.x, -sceneDirection.z, sceneDirection.y);
    };

    const resetCamera = () => {
        switch (currentCamera.type) {
            case CameraMode.POV:
                resetPov();
                break;
            case CameraMode.STANDARD:
            case CameraMode.TOP:
            case CameraMode.ORTHO:
                viewer.cameraHelper.resetStandard(currentCamera.type as CameraMode);
                break;

            default:
                throw new Error(`Unknown camera mode: ${currentCamera.type}`);
        }
    };

    useEffect(() => {
        if (currentCamera.type !== CameraMode.POV) return;
        switch (POVCameraType) {
            case POVType.DATA:
                applyDataPov();
                return;
            case POVType.LOCKED:
            case POVType.HORIZONTAL:
            case POVType.VERTICAL:
            case POVType.FREE:
                applyTrackedPov();
                return;
        }
    }, [x, y, z, POVCameraType, currentCamera.type]);

    const applyDataPov = () => {
        const sensorPoint = getSelectedSensorPoint();
        if (!sensorPoint) return;

        const center = sensorPoint.getCenter();
        const { ID } = sensorPoint;

        const sensorData = getGrid().getActiveLayer().getActiveFrame().getSensors()[ID];

        const direction = sensorData?.getVectorialResult()?.normalize() ?? new FVector();

        const pov = viewer.cameraHelper.getPovCamera();

        // Reset locks to none & update direction:
        pov.updateLocks(false, false);
        pov.setPositionAndTarget(center, direction);

        // Lock completely:
        updatePovMode(POVCameraType);
    };

    const applyTrackedPov = () => {
        const sensorPoint = getSelectedSensorPoint();
        if (!sensorPoint) return;

        const center = sensorPoint.getCenter();
        const direction = getCurrentPovDirection();
        const pov = viewer.cameraHelper.getPovCamera();

        pov.updateLocks(false, false);
        pov.setPositionAndTarget(center, direction);
        updatePovMode(POVCameraType);
    };

    const handleOpenCameraDialog = () => setCameraDialogOpen(true);
    const handleCloseCameraDialog = () => setCameraDialogOpen(false);

    const handleSelectCamera = async (cameraId: string) => {
        try {
            const cameraData = await getCamera(cameraId);
            const coordinates = inferStoredCoordinateSystem(cameraData.position, cameraData.target);
            const localPosition = coordinates === 'geo' ? fromCurrentSceneCoordinate(cameraData.position) : cameraData.position;
            const localTarget = coordinates === 'geo' ? fromCurrentSceneCoordinate(cameraData.target) : cameraData.target;

            const newCamera = {
                type: cameraData.type,
                position: localPosition,
                target: localTarget,
            };
            viewer.cameraHelper.setCameraMode(newCamera, cameraData.type, true);
            showNotification(`Loaded camera ${cameraData.name}`, NotificationType.Success);
        } catch (error) {
            console.error('Failed to load camera', error);
            showNotification('Failed to load camera', NotificationType.Error);
            throw error;
        }
    };

    const handleOpenSaveCameraDialog = () => setSaveDialogOpen(true);

    const handleConfirmSaveCamera = async (name: string) => {
        try {
            const { position, target } = viewer.cameraHelper.refreshStore(currentCamera.type as CameraMode);
            const geoPosition = toCurrentSceneCoordinate(position);
            const geoTarget = toCurrentSceneCoordinate(target);

            const payload = {
                name,
                type: currentCamera.type as CameraMode,
                position: geoPosition,
                target: geoTarget,
            };

            const createdCamera = await createCamera(payload);
            console.log('Camera saved', createdCamera);
            showNotification(`Camera ${createdCamera.name} saved`, NotificationType.Success);
        } catch (error) {
            console.error('Failed to save camera', error);
            showNotification('Failed to save camera', NotificationType.Error);
            throw error;
        }
    };

    const exportCamera = () => {
        const { position, target } = viewer.cameraHelper.refreshStore(currentCamera.type as CameraMode);
        const geoPosition = toCurrentSceneCoordinate(position);
        const geoTarget = toCurrentSceneCoordinate(target);
        const camera = {
            type: currentCamera.type,
            position: geoPosition,
            target: geoTarget,
        };
        localStorage.setItem(exported_camera_key, JSON.stringify(camera));
        setIsExportingCamera(true);
        showNotification('Camera exported! You can now import it in another session', NotificationType.Success);
    };

    const importCamera = () => {
        const imported = localStorage.getItem(exported_camera_key);
        if (imported) {
            const parsed = JSON.parse(imported) as CameraState;
            const coordinates = inferStoredCoordinateSystem(parsed.position, parsed.target);
            const cam: CameraState =
                coordinates === 'geo'
                    ? {
                          ...parsed,
                          position: fromCurrentSceneCoordinate(parsed.position),
                          target: fromCurrentSceneCoordinate(parsed.target),
                      }
                    : parsed;
            setCurrentCamera(cam);
            viewer.cameraHelper.setCameraMode(cam, cam.type as CameraMode, true);
            localStorage.removeItem(exported_camera_key);
            setIsExportingCamera(false);
        } else {
            showNotification('No camera found to import', NotificationType.Warning);
        }
    };

    if (!showContent) return null;

    function formatCameraLabel(type: CameraMode): string {
        switch (type) {
            case CameraMode.STANDARD:
                return 'Std';
            case CameraMode.ORTHO:
                return 'Axo';
            default:
                return type.charAt(0).toLocaleUpperCase() + type.slice(1).toLocaleLowerCase();
        }
    }

    return (
        <>
            <CustomTooltip side="left" mode="icon" title={getSentence('Camera.Copy.Paste.Tooltip')}>
                <Typography sx={{ fontWeight: 'bold', mb: 0.5 }}>Manage Cameras</Typography>
            </CustomTooltip>
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Tooltip title={!capabilities.canManageCamera ? getDisabledInEnvMessage(env) : ''}>
                    <span>
                        <Button variant="outlined" onClick={handleOpenCameraDialog} disabled={!capabilities.canManageCamera}>
                            Load
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={!capabilities.canManageCamera ? getDisabledInEnvMessage(env) : ''}>
                    <span>
                        <Button variant="outlined" onClick={handleOpenSaveCameraDialog} disabled={!capabilities.canManageCamera}>
                            Save
                        </Button>
                    </span>
                </Tooltip>
                <Button id="exportCamera" variant="outlined" onClick={() => exportCamera()}>
                    Copy
                </Button>
                <Button id="importCamera" variant="outlined" disabled={!isExportingCamera} onClick={() => importCamera()}>
                    Paste
                </Button>
                <Button onClick={resetCamera} variant="contained">
                    Reset
                </Button>
                <Button variant={orbitate ? 'contained' : 'outlined'} onClick={() => setOrbitate((v) => !v)}>
                    Orbit
                </Button>
            </Box>
            <FormControl>
                <Typography fontWeight={'bold'}>Mode</Typography>
                <Box sx={{ minHeight: '25px', display: 'flex', alignItems: 'center' }}>
                    <RadioGroup
                        sx={{ gap: 1.2 }}
                        row
                        value={currentCamera.type}
                        onChange={(e) => handleCameraMode(e.target.value as CameraMode)}
                        className="panel-compact-control-stack"
                    >
                        {Object.values(CameraMode).map((type: any) => (
                            <FormControlLabel key={type} value={type} control={<Radio size="small" />} label={formatCameraLabel(type)} />
                        ))}
                    </RadioGroup>
                    {currentCamera.type === CameraMode.POV && (
                        <Select
                            size="small"
                            sx={{ ml: 0.3, width: '15px' }}
                            variant="standard"
                            displayEmpty
                            renderValue={(selected) => (selected ? '' : '')}
                            value={POVCameraType}
                            onChange={(e) => setPOVCameraType(e.target.value as POVType)}
                        >
                            <MenuItem value={POVType.DATA}>
                                <CustomTooltip
                                    title="Camera gets the direction from the currently selected Sensor Point."
                                    disableInteractive
                                    leaveDelay={0}
                                >
                                    <span>Data</span>
                                </CustomTooltip>
                            </MenuItem>

                            <MenuItem value={POVType.FREE}>
                                <CustomTooltip
                                    title="Camera can be rotated freely by the user. Maintained also if you change the selected Sensor Point."
                                    disableInteractive
                                    leaveDelay={0}
                                >
                                    <span>Free</span>
                                </CustomTooltip>
                            </MenuItem>

                            <MenuItem value={POVType.HORIZONTAL}>
                                <CustomTooltip title="Camera allows to rotate horizontally." disableInteractive leaveDelay={0}>
                                    <span>Horizontal</span>
                                </CustomTooltip>
                            </MenuItem>

                            <MenuItem value={POVType.VERTICAL}>
                                <CustomTooltip title="Camera allows to rotate vertically." disableInteractive leaveDelay={0}>
                                    <span>Vertical</span>
                                </CustomTooltip>
                            </MenuItem>

                            <MenuItem value={POVType.LOCKED}>
                                <CustomTooltip title="Camera gets locked in the current direction." disableInteractive leaveDelay={0}>
                                    <span>Locked</span>
                                </CustomTooltip>
                            </MenuItem>
                        </Select>
                    )}
                </Box>
            </FormControl>

            <CameraDialog open={cameraDialogOpen} onClose={handleCloseCameraDialog} onSelectCamera={handleSelectCamera} />
            <SaveCameraDialog open={saveDialogOpen} onCancel={() => setSaveDialogOpen(false)} onSave={handleConfirmSaveCamera} />

            <Box sx={{ mt: 1 }}>
                <Button
                    sx={{ mb: 1 }}
                    size="small"
                    variant={showAdvanced ? 'outlined' : 'contained'}
                    onClick={() => setShowAdvanced((prev) => !prev)}
                >
                    Advanced
                </Button>
                {showAdvanced && (
                    <FormGroup className="panel-compact-control-stack">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={ambient.wasdEnabled}
                                    onChange={(e) => setAmbientPanel({ wasdEnabled: e.target.checked })}
                                />
                            }
                            label="Enable WASD/Arrows"
                        />
                        {ambient.wasdEnabled && (
                            <>
                                <Typography sx={{ mt: 0.4 }}>WASD/Arrows speed</Typography>
                                <Slider
                                    className="panel-slider"
                                    size="small"
                                    sx={{ ml: 0.8, mt: 0.4 }}
                                    valueLabelDisplay="on"
                                    min={1}
                                    max={10}
                                    step={1}
                                    value={ambient.cameraWasdSpeed}
                                    onChange={(_, v) => setAmbientPanel({ cameraWasdSpeed: Number(v) })}
                                />
                            </>
                        )}
                    </FormGroup>
                )}
                <Divider sx={{ mt: 1, mb: 1 }}></Divider>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography sx={{ fontWeight: 'bold' }}>Navigation Hints</Typography>
                    <Typography variant="body2">• Left-click to rotate</Typography>
                    <Typography variant="body2">• Ctrl+Click to select a sensor point</Typography>
                    <Typography variant="body2">• Right-click or Shift/Ctrl+Left-click to pan</Typography>
                    <Typography variant="body2">• Double-click to reset camera (To Be Implemented)</Typography>
                    <Typography variant="body2">• WASD or Arrow Keys to move camera</Typography>
                    <Typography variant="body2">• Shift+WASD/Arrows for faster movements</Typography>
                </Box>
            </Box>
        </>
    );
};

export default memo(Camera);
